import { graphql } from 'app/generated/gql'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from 'urql'
import { LocationJson } from 'app/types/entities'
import type { SortDirection } from 'app/generated/graphql'

export type SubmissionData = Record<string, { count: number; last?: string }>

export type OnlineSchedulerLoaderData = {
  locations: { hits: LocationJson[]; total: number }
  submissions: SubmissionData
}

export type UseOnlineSchedulerReturn = {
  locations: { hits: LocationJson[]; total: number }
  submissions: SubmissionData
}

const ONLINE_SCHEDULER_LOCATIONS_QUERY = graphql(`
  query OnlineSchedulerLocations(
    $search: String
    $pagination: PaginationInput
    $sort: SortInput
    $schedulerSort: SortInput
  ) {
    locations(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        name
        isSchedulerLive
      }
      total
      page
    }
    schedulers(pagination: { limit: 1000 }, sort: $schedulerSort) {
      hits {
        locationId
        createdDate
      }
    }
  }
`)

export function useOnlineScheduler(): UseOnlineSchedulerReturn {
  const [searchParams] = useSearchParams()

  const sort = searchParams.get(`sort`)
  const query = searchParams.get(`query`)
  const page = searchParams.get(`page`)
  const sortParts = sort?.split(`:`)

  const [result] = useQuery({
    query: ONLINE_SCHEDULER_LOCATIONS_QUERY,
    variables: {
      search: query || undefined,
      pagination: page ? { page: Number(page) } : undefined,
      sort:
        sortParts?.length === 2
          ? { field: sortParts[0], direction: sortParts[1].toUpperCase() as SortDirection }
          : undefined,
      schedulerSort: { field: 'createdDate', direction: 'DESC' },
    },
  })

  const locations = useMemo(
    () =>
      result.data?.locations
        ? {
            hits: result.data.locations.hits.map((location) => ({
              ...location,
              isSchedulerLive: location.isSchedulerLive ?? undefined,
            })),
            total: result.data.locations.total,
          }
        : { hits: [], total: 0 },
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [result.data?.locations],
  )

  const submissions: SubmissionData = useMemo(() => {
    const schedulers = result.data?.schedulers?.hits ?? []
    const map: SubmissionData = {}
    for (const s of schedulers) {
      if (!s.locationId) continue
      if (!map[s.locationId]) {
        map[s.locationId] = { count: 0, last: s.createdDate }
      }
      map[s.locationId].count++
      if (s.createdDate > (map[s.locationId].last ?? ``)) {
        map[s.locationId].last = s.createdDate
      }
    }
    return map
  }, [result.data?.schedulers?.hits])

  return { locations, submissions }
}
